class FaqPage extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.search = this.querySelector('[data-faq-search]');
    this.scope = this.closest('main') || document;
    this.categories = [...this.querySelectorAll('[data-faq-category]')];
    this.status = this.querySelector('[data-faq-status]');
    this.empty = this.querySelector('[data-faq-empty]');
    this.category = 'all';
    this.expanded = false;
    this.refresh();

    this.querySelectorAll('[data-faq-tools]').forEach((element) => { element.hidden = false; });
    this.search.addEventListener('input', () => this.filter(), { signal });
    this.categories.forEach((button) => {
      button.addEventListener('click', () => {
        this.category = button.dataset.faqCategory;
        if (this.category === 'all') this.expanded = true;
        this.filter();
      }, { signal });
    });
    this.querySelectorAll('[data-faq-reset]').forEach((button) => {
      button.addEventListener('click', () => {
        this.reset();
        this.querySelector('[data-faq-heading]').focus();
      }, { signal });
    });
    this.querySelector('[data-faq-show-all]').addEventListener('click', () => {
      this.reset();
      const heading = this.groups[0]?.querySelector('[data-faq-heading]');
      (heading || this.querySelector('[data-faq-heading]')).focus();
    }, { signal });

    // Sections remain independently editable while sharing one search and filter.
    this.scope.addEventListener('shopify:section:load', () => this.refresh(), { signal });
    this.scope.addEventListener('shopify:section:unload', (event) => this.refresh(event.target), { signal });

    // Reveal a question selected in the theme editor, even when a filter hid it.
    this.scope.addEventListener('shopify:block:select', (event) => {
      const question = event.target.closest('[data-faq-question]');
      if (!question || !this.questions.includes(question)) return;
      this.reset();
      question.open = true;
    }, { signal });
    this.scope.addEventListener('shopify:section:select', (event) => {
      if (event.target.querySelector('[data-faq-group]')) this.reset();
    }, { signal });
  }

  disconnectedCallback() {
    this.controller?.abort();
    // Leave every answer accessible if the search section is removed in the editor.
    this.questions?.forEach((question) => { question.hidden = false; });
    this.groups?.forEach((group) => { group.hidden = false; });
  }

  refresh(excludedSection) {
    this.groups = [...this.scope.querySelectorAll('[data-faq-group]')]
      .filter((group) => !excludedSection?.contains(group));
    this.questions = [...this.querySelectorAll('[data-faq-question]'),
      ...this.groups.flatMap((group) => [...group.querySelectorAll('[data-faq-question]')])];
    this.searchIndex = new Map(this.questions.map((question) => [question, this.normalize(question.textContent)]));
    const controls = [this.querySelector('.faq-page__questions'),
      ...this.groups.map((group) => group.querySelector('.faq-page__questions'))]
      .map((container) => container.id).join(' ');
    this.search.setAttribute('aria-controls', controls);
    this.categories.forEach((button) => {
      button.setAttribute('aria-controls', controls);
      button.hidden = button.dataset.faqCategory !== 'all' &&
        !this.questions.some((question) => question.dataset.category === button.dataset.faqCategory);
    });
    if (!this.categories.some((button) => !button.hidden && button.dataset.faqCategory === this.category)) {
      this.category = 'all';
    }
    this.filter();
  }

  normalize(text) {
    return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('de').replace(/ß/g, 'ss');
  }

  reset() {
    this.search.value = '';
    this.category = 'all';
    this.expanded = true;
    this.filter();
  }

  filter() {
    const terms = this.normalize(this.search.value).trim().split(/\s+/).filter(Boolean);
    const showLibrary = this.expanded || terms.length > 0 || this.category !== 'all';
    let count = 0;
    this.questions.forEach((question) => {
      const matchesCategory = this.category === 'all' || question.dataset.category === this.category;
      const matchesSearch = terms.every((term) => this.searchIndex.get(question).includes(term));
      const inVisibleSection = showLibrary || this.contains(question);
      question.hidden = !(matchesCategory && matchesSearch && inVisibleSection);
      if (!question.hidden) count += 1;
    });
    this.groups.forEach((group) => {
      group.hidden = ![...group.querySelectorAll('[data-faq-question]')].some((question) => !question.hidden);
    });
    const hasFeaturedResults = this.questions.some((question) => this.contains(question) && !question.hidden);
    const layout = this.querySelector('.faq-page__layout');
    layout.hidden = count > 0 && !hasFeaturedResults;
    this.querySelector('[data-faq-heading]').hidden = count > 0 && !hasFeaturedResults;
    this.categories.forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.faqCategory === this.category));
    });
    this.empty.hidden = count > 0;
    this.status.textContent = `${this.status.dataset.resultsLabel} ${count}`;
  }
}

if (!customElements.get('faq-page')) customElements.define('faq-page', FaqPage);
